'use client';

import { useState } from 'react';
import type { AmountDenomination } from '@/lib/amount-denomination';

/**
 * Keeps the amounts a form holds as typed text worth what they were typed as,
 * through a change of the currency the theme converts in: a rate that arrives
 * for the seller's currency while the fields showed AUD (see the rates comment
 * in src/context/theme-context.tsx), or another currency picked in another tab.
 * See src/lib/amount-denomination.ts.
 *
 * `current` is the theme's denomination now, currentDenomination(displayCurrency,
 * toDisplayAmount). `initial` is the one the form's amounts started in: a
 * draft's, or null for `current`. Returns the denomination the amounts are in,
 * for a draft to record beside them.
 *
 * When the currency changes, `restate(from, to)` is called DURING RENDER, and
 * it may only set this component's own state (through redenominateAmount).
 * React then renders the component again before committing anything, so no
 * commit, effect or click ever pairs the new rate with an amount still typed
 * in the old currency. A rate that merely moved restates nothing.
 */
export function useHeldDenomination(
  current: AmountDenomination,
  initial: () => AmountDenomination | null,
  restate: (from: AmountDenomination, to: AmountDenomination) => void,
): AmountDenomination {
  const [held, setHeld] = useState<AmountDenomination>(() => initial() ?? current);
  if (held.currency !== current.currency || held.rate !== current.rate) {
    setHeld(current);
    if (held.currency !== current.currency) restate(held, current);
  }
  return held;
}
